import { MAINTENANCE } from '../config/maintenance.js';

/**
 * Pure maintenance calculations shared by MaintenanceSystem and the UI.
 */

const DAY_MS = 86400000;

function basePrice(ship) {
  return ship.type?.basePurchasePrice || 0;
}

/** Years between mandatory dry dockings for this ship. */
export function getDrydockIntervalYears(ship) {
  const teu = ship.type?.cargoCapacity || 0;
  return MAINTENANCE.DRYDOCK_INTERVAL_YEARS.find(r => teu <= r.maxTeu).years;
}

export function getDrydockCost(ship) {
  return Math.round(basePrice(ship) * MAINTENANCE.DRYDOCK_COST_PCT);
}

export function getDrydockDays(ship) {
  const teu = ship.type?.cargoCapacity || 0;
  return Math.ceil(MAINTENANCE.DRYDOCK_BASE_DAYS + teu / 1000 * MAINTENANCE.DRYDOCK_DAYS_PER_1000_TEU);
}

export function getSurveyFee(ship) {
  return Math.round(basePrice(ship) * MAINTENANCE.SURVEY_FEE_PCT);
}

export function getDetentionFine(ship) {
  return Math.round(basePrice(ship) * MAINTENANCE.DETENTION_FINE_PCT);
}

/** Whole days until `iso` (negative when overdue); null when unscheduled. */
export function daysUntil(iso, now) {
  if (!iso || !now) return null;
  return Math.floor((new Date(iso).getTime() - now.getTime()) / DAY_MS);
}

/** Daily fine for an overdue dry dock (0 when not overdue). */
export function getDrydockOverdueFine(ship, now) {
  const left = daysUntil(ship.maintenance?.drydockDue, now);
  if (left === null || left >= 0) return 0;
  const escalate = -left > MAINTENANCE.DRYDOCK_OVERDUE_ESCALATION_DAYS
    ? MAINTENANCE.DRYDOCK_OVERDUE_ESCALATION_MULT : 1;
  return Math.round(basePrice(ship) * MAINTENANCE.DRYDOCK_OVERDUE_FINE_PCT * escalate);
}

/** Daily fine for a survey overdue past its grace period (0 otherwise). */
export function getSurveyOverdueFine(ship, now) {
  const left = daysUntil(ship.maintenance?.surveyDue, now);
  if (left === null || -left <= MAINTENANCE.SURVEY_GRACE_DAYS) return 0;
  return Math.round(basePrice(ship) * MAINTENANCE.SURVEY_OVERDUE_FINE_PCT);
}

export function isSurveyDue(ship, now) {
  const left = daysUntil(ship.maintenance?.surveyDue, now);
  return left !== null && left < 0;
}

export function isDrydockOverdue(ship, now) {
  const left = daysUntil(ship.maintenance?.drydockDue, now);
  return left !== null && left < 0;
}

/** Chance of a random port state control inspection when docking at `port`. */
export function getInspectionChance(ship, port, now) {
  if (!port?.services?.repair) return 0;
  let chance = MAINTENANCE.INSPECTION_CHANCE[port.size] || 0;
  if (ship.condition < MAINTENANCE.INSPECTION_POOR_CONDITION) chance *= 2;
  if (isSurveyDue(ship, now) || isDrydockOverdue(ship, now)) chance *= MAINTENANCE.INSPECTION_OVERDUE_MULT;
  return Math.min(1, chance);
}

/** A fresh schedule starting at `now`: survey in a year, dry dock after its interval. */
export function newMaintenanceSchedule(ship, now) {
  const years = getDrydockIntervalYears(ship);
  return {
    surveyDue:    new Date(now.getTime() + MAINTENANCE.SURVEY_INTERVAL_DAYS * DAY_MS).toISOString(),
    drydockDue:   new Date(now.getTime() + years * 365 * DAY_MS).toISOString(),
    drydockUntil: null,
    detention:    null
  };
}
